import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';

import PdfExamViewer from './PdfExamViewer';
import { useAuth } from '../context/AuthContext';
import { parseApiError } from '../lib/apiErrors';

/**
 * View-only modal for a student's homework PDF (homework uploads are PDF-only).
 * `fetchBlob` is an async () => Blob call against the authenticated route.
 * Watermarked with the VIEWER's name/id/time; no download control. Client-side
 * only, like CorrectedPaperViewer.
 */
export default function HomeworkFileViewerModal({ title, fetchBlob, onClose }) {
  const { user } = useAuth();
  const [blobUrl, setBlobUrl] = useState(null);
  const [error, setError] = useState(null);
  const [viewedAt] = useState(() => new Date().toLocaleString());

  useEffect(() => {
    let cancelled = false;
    let url = null;
    fetchBlob()
      .then((blob) => {
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setBlobUrl(url);
      })
      .catch((err) => {
        if (!cancelled) setError(parseApiError(err, 'Could not load the file').message);
      });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
    // fetchBlob identity is intentionally ignored: one fetch per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const watermark = useMemo(
    () => ({ name: user?.name || 'Viewer', id: String(user?.id || user?.user_id || ''), timestamp: viewedAt }),
    [user?.name, user?.id, user?.user_id, viewedAt]
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-3"
      onClick={onClose}
      data-testid="homework-viewer-modal"
    >
      <div
        className="flex max-h-[92vh] w-full max-w-3xl flex-col rounded-lg bg-white shadow-xl dark:bg-slate-800"
        role="dialog"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3 dark:border-slate-700">
          <h3 className="min-w-0 truncate text-sm font-semibold text-slate-900 dark:text-white">{title}</h3>
          <button
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700"
            data-testid="homework-viewer-close"
          >
            <X size={18} />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-3">
          {error ? (
            <p className="text-sm text-red-500">{error}</p>
          ) : !blobUrl ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>
          ) : (
            <PdfExamViewer src={blobUrl} title={title} watermark={watermark} className="max-h-[78vh]" />
          )}
        </div>
      </div>
    </div>
  );
}
